import { type User, type UserGroupLink } from '@kalos/kalos-rpc';
import { Dialog, DialogContent, DialogHeader, DialogTitle, LoadingIcon } from '@kalos/ui';
import { type FC } from 'react';

import { useUserGroupLinksQuery } from '../../../hooks/react-query/useUserGroupLinksQuery';
import { CustomerEditForm } from './CustomerEditForm';

interface CustomerEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customer: User) => void;
  customer: User;
  /** Omit on edit to have the dialog load the customer's links itself. */
  groupLinks?: UserGroupLink[];
  mode?: 'create' | 'edit';
}

export const CustomerEditDialog: FC<CustomerEditDialogProps> = ({
  open,
  onClose,
  onSave,
  customer,
  groupLinks,
  mode = 'edit',
}) => {
  const shouldFetchGroupLinks = open && mode === 'edit' && !groupLinks && customer.id > 0;
  const groupLinksQuery = useUserGroupLinksQuery({
    enabled: shouldFetchGroupLinks,
    filter: { userId: customer.id },
    select: (data) => data?.results ?? [],
  });

  // The form seeds its group selection once on mount, so it must not render before the links land.
  const resolvedGroupLinks = groupLinks ?? (shouldFetchGroupLinks ? groupLinksQuery.data : []);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add Customer' : 'Edit Customer Information'}
          </DialogTitle>
        </DialogHeader>
        {open &&
          (resolvedGroupLinks ? (
            <CustomerEditForm
              customer={customer}
              groupLinks={resolvedGroupLinks}
              mode={mode}
              onSave={(saved) => {
                onSave(saved);
                onClose();
              }}
              onCancel={onClose}
            />
          ) : (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-600">
              <LoadingIcon className="h-5 w-5" />
              Loading customer…
            </div>
          ))}
      </DialogContent>
    </Dialog>
  );
};
